import { useState } from "react";

import { Button } from "@mui/material";
import { useAlertContext } from "@shared";

import { EdictTransferResponse } from "@/api";
import { EdictTransferResultDialog } from "@/common/components/edict-transfer/EdictTransferResultDialog";
import { useTransferEdict } from "@/common/components/edict-transfer/hooks/useTransferEdict";
import { useTranslation } from "@/translations";

import { useCaseNoteEdictQuery } from "./hooks/useCaseNoteEdictQuery";

interface Props {
  clientId: string;
  caseNoteId: string;
}

export function OpenSrvButton({ clientId, caseNoteId }: Props) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const [transferResult, setTransferResult] = useState<EdictTransferResponse | null>(null);

  const { data, isError, isFetching, refetch } = useCaseNoteEdictQuery(clientId, caseNoteId);
  const { mutate: transferEdict, isPending: isTransferring } = useTransferEdict(setTransferResult);

  const handleClick = async () => {
    let sourceReference = data?.sourceReference ?? null;

    if (isError) {
      const { data: refetched, isError: stillFailing } = await refetch();
      if (stillFailing) {
        showError(t("clients.client.caseNotes.openSrvLoadFailed"));
        return;
      }
      sourceReference = refetched?.sourceReference ?? null;
    }

    if (sourceReference === null) {
      showError(t("clients.client.caseNotes.openSrvUnavailable"));
      return;
    }

    transferEdict({ clientId, sourceReference });
  };

  return (
    <>
      <Button variant="outlined" size="small" loading={isFetching || isTransferring} onClick={() => void handleClick()}>
        {t("clients.client.caseNotes.openSrv")}
      </Button>
      {transferResult !== null && (
        <EdictTransferResultDialog result={transferResult} onClose={() => setTransferResult(null)} />
      )}
    </>
  );
}
